import ChannelLineUpProvider, {
  ChannelFeed,
  KeyValuePair,
  PACKAGE_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import ReportingService from "../../../src/services/ReportingService";
import { ComponentNames } from "../../constant";
import { chnlPkgReference } from "../../../src/utils/UtilityFunctions";
import {
  addMySportsBetaGenrePackage,
  mySportsBetaPkgFilter
} from "../../../src/utils/genreUtility";
import { parseChannelCompareTheme } from "../../helper/channel-compare-tool-theme";
import { logAdapterEvent, logError } from "../../helper/logger";
import { pickChannels, pickChrome, orderChannelCategoryConfig, isExcludedFastChannel, mergeChannelsAcrossPlatforms, isUsingServiceChannels, resolveChannelCategoryKey, resolveEntertainmentCategoryKey, buildChannelCategoryMembership } from "../../helper/channels-service-helpers";

const ADAPTER_NAME = "ChannelCompareToolPageAdapter";

interface IChannelCompareToolPageAdapterArgs {
  content: any;
  componentId: string;
  cmsPageName: string;
  index: number;
  queryParams?: any;
  myFreeDTVConfig?: any;
  mySportsEligibleCookie?: any;
  msbNationalRollOut?: any;
  domName?: string;
  embeddedSvods?: any;
  dtviEmbeddedSvodOffers?: any;
  dtvsEmbeddedSvodOffers?: any;
  isEmbeddedSvodsEnabled?: boolean;
  packagesConfig?: any;
  rsnTierLegalTextObj?: any;
  genreNationals?: any;
  /**
   * channels-service v4 envelope `{ platform, lastUpdated, channels[] }` for the
   * stream platform.
   */
  channelsServiceData?: any;
  /**
   * channels-service v4 envelope for the satellite platform. The CCT modal
   * renders columns for stream + satellite packages on the same page (Akamai
   * redirect makes `/satellite/channel-lineup/modal` resolve here too), so the
   * adapter unions stream + satellite channels deduped by `channelNameLabel`.
   * Optional — when missing, only `channelsServiceData` is used.
   */
  channelsServiceSatelliteData?: any;
  /**
   * Raw AEM compare channels (`chnlCmpToolData.elements.channels`) — degraded
   * fallback when the service is enabled but empty. Flag OFF, content.channels
   * holds the AEM payload; flag ON content carries none.
   */
  aemChannels?: any;
  /**
   * Raw AEM compare `channelCategories` (slug-keyed options) — the bucketing
   * config when channels come from AEM (flag-OFF/degraded), same as production.
   */
  aemChannelCategories?: any;
  /**
   * Grid/CCT config from the global Storyblok package-config feed. When present
   * (flag ON) these supply the grid fields; when absent (flag OFF / AEM path) the
   * adapter falls back to `content.*`.
   */
  globalLineupConfig?: any;
}

export const ChannelCompareToolPageAdapter = ({
  content,
  componentId,
  cmsPageName,
  index,
  queryParams,
  myFreeDTVConfig,
  mySportsEligibleCookie,
  msbNationalRollOut,
  domName,
  embeddedSvods,
  dtviEmbeddedSvodOffers,
  dtvsEmbeddedSvodOffers,
  isEmbeddedSvodsEnabled,
  packagesConfig,
  rsnTierLegalTextObj,
  genreNationals,
  channelsServiceData,
  channelsServiceSatelliteData,
  aemChannels,
  aemChannelCategories,
  globalLineupConfig
}: IChannelCompareToolPageAdapterArgs): any => {
  try {
    logAdapterEvent(ADAPTER_NAME, "Starting channel compare tool adaptation", {
      cmsPageName,
      componentId,
      index,
      queryParams
    });

    let fixedContent: any;
    if (content) {
      // Resolve a grid/CCT field: prefer the global package-config feed (flag ON),
      // fall back to content.* (flag OFF / AEM). `packages` is sourced WHOLE here;
      // downstream queryParams filters (Spanish prefix, satellite ATT_TV_→DTV_
      // remap) narrow it per request.
      const resolveField = (key: string) =>
        pickChrome(globalLineupConfig?.[key], content?.[key]);
      // Category-bucketing config follows the channel SOURCE (Option C): the prose
      // package-config blok [{label, engCategories[]}] ONLY when the service supplies
      // channels (either platform). When channels come from AEM (flag-OFF OR degraded),
      // fall to the AEM channelCategories (slug options) below so the categoryId slug
      // buckets — production behavior. Mirrors pickChannels so config + channels align.
      const categoryConfig = isUsingServiceChannels(channelsServiceData?.channels, channelsServiceSatelliteData?.channels)
        ? globalLineupConfig?.categoryConfig
        : undefined;
      // Ordered category config + Entertainment fallback for the channel-side
      // categoryId resolution. The modal's DDS provider buckets each channel by
      // scalar `category` (= categoryId) EQUALITY against the accordion KEY, which
      // is the label (channelCategories below emits a "label,label" CSV). So a
      // flag-ON service channel — no scalar categoryId — must resolve to a CONTENT
      // accordion label, falling to the Entertainment label when its categories[]
      // carry only FORMAT values ("4K"/"Mixed Authorization"). Same principle as
      // the lineup/interactive Entertainment fallback, fitted to the modal's
      // label-equality seam: resolveChannelCategoryKey returns the matched
      // accordion's label, resolveEntertainmentCategoryKey the Entertainment label
      // (both from the SAME ordered config the accordions derive from). Flag-OFF /
      // degraded: categoryConfig is undefined, so these are [] / undefined and
      // parseChannelsData keeps the channel's own categoryId slug unchanged.
      const orderedCategoryConfig = orderChannelCategoryConfig(categoryConfig ?? []);
      const entertainmentCategoryKey = resolveEntertainmentCategoryKey(orderedCategoryConfig);
      const resolvedPackages = resolveField("packages");
      const resolvedGenrePackages = resolveField("genrePackages");
      const resolvedPackageCategories = resolveField("packageCategories");
      // Sort options for the modal grid are read from the modal's OWN story
      // (`channel-lineup/modal` -> channelComparisonTool blok, flag ON) / AEM file
      // (chlupCmprModalData, flag OFF) via `content`, NOT the shared package-config
      // feed: the modal needs different sort options (Name/Channel number/Category)
      // than the lineup pages (A-Z/Category), so each surface owns its own fields.
      const resolvedSortOptions = content?.sortOptions;
      const resolvedDefaultSortOptions = content?.defaultSortOptions;

      let packages = [...(resolvedPackages ?? [])];
      packages = addMySportsBetaGenrePackage(
        packages,
        resolvedGenrePackages
      );

      const mySportsFlag = false;

      const isChannelLineUpPage = cmsPageName === "channels";
      packages = mySportsBetaPkgFilter(
        packages,
        queryParams,
        isChannelLineUpPage,
        msbNationalRollOut
      );

      packages = packages.map(pkg => {
        if (
          domName === "streamtv" &&
          queryParams?.lang?.toLowerCase?.() === "spanish" &&
          queryParams.channels !== "satellite"
        ) {
          if (pkg?.packageId === "DTV_OPTIMO_MAS") {
            pkg.packageId = "ATT_TV_OPTIMO_MAS";
          }
          if (pkg?.packageId === "ATT_TV_OPTIMO_MAS") {
            pkg.edspPackage = true;
          }
        }
        // packageIndicatorIndex is assigned AFTER the platform filter below, not
        // here over the whole catalog — see the per-categoryId reindex after the
        // platformPrefix filter.
        return pkg;
      });

      const packageIds = packages.map(pack => pack.packageId);
      // Channels source: union the stream + satellite envelopes (the modal renders
      // both platforms' columns), deduped by label. pickChannels routes to AEM
      // (content.channels) flag-OFF and when the service is empty (degraded fallback).
      const channelsForFilter = pickChannels(
        mergeChannelsAcrossPlatforms(channelsServiceData?.channels, channelsServiceSatelliteData?.channels),
        aemChannels ?? content?.channels
      );
      // Satellite-only view of the compare tool (Akamai routes
      // /satellite/channel-lineup/modal here with channels=satellite). FAST
      // channels are app/online-only and not receivable on satellite, so they
      // are excluded when the tool is showing the satellite lineup (WAT-10783).
      // In the mixed stream+satellite modal they are left to existing logic.
      const isSatelliteContext =
        queryParams?.channels === "satellite" || content?.serviceId === "dtvs";
      const filteredChannels = filterChannels({
        ...content,
        packages,
        channels: channelsForFilter,
        isSatelliteContext
      });
      const packageCategories = ChannelLineUpProvider.getKVsFromStrings(
        resolvedPackageCategories?.options ?? []
      );
      const langQP = queryParams && queryParams.lang;
      // Filter packages to the platform the modal is showing — satellite when
      // ?channels=satellite (or a dtvs content context), stream (ATT_TV_) otherwise
      // (default / internet). The flag-ON source (globalLineupConfig.packages) is the
      // WHOLE package-config catalog (ATT_TV_ stream + DTV_ satellite ids); prod and
      // the lineup page render ONE platform, never merged. Mirrors
      // channel-lineup-page-adapter.ts's platformPrefix filter; runs after the Spanish
      // OPTIMO_MAS id-swap above and before the satellite ATT_TV_->DTV_ remap below
      // (which then no-ops on the DTV-only set). Was gated on lang==="spanish", so
      // English requests skipped it and rendered both platforms merged.
      const platformPrefix = isSatelliteContext ? "DTV_" : "ATT_TV_";
      packages = packages?.filter(pkg => pkg?.packageId?.includes(platformPrefix));
      // packageIndicatorIndex = the package's position WITHIN its own categoryId
      // (language) group, assigned AFTER the platform filter. parseChannelsData
      // builds packageIndicators["2yr"][lang] dense over packages.filter(p =>
      // p.categoryId === lang); the grid reads ctgryPkgIndicators[pkg.packageIndicatorIndex]
      // against that same per-language array. A whole-catalog index left satellite
      // columns at global positions [4..7]/[8..11] (stream occupied 0-3), reading
      // past the length-4 array -> undefined (blank/0-row modal). Per-language 0-based
      // positions align both groups (English 0-3, Spanish 0-3); ATT_TV_ stream is
      // unchanged (already 0-3). Mirrors the lineup parser's index/array contract.
      const categoryIndexCounters: Record<string, number> = {};
      packages = packages?.map(pkg => {
        const key = pkg?.categoryId ?? "";
        const indicatorIndex = categoryIndexCounters[key] ?? 0;
        categoryIndexCounters[key] = indicatorIndex + 1;
        return { ...pkg, packageIndicatorIndex: indicatorIndex };
      });
      // note clean up the channel data for channel listing page
      if (cmsPageName === "channels") {
        packages = packages.map((pck: any) => {
          if (pck.byodCta) {
            pck.cta = pck.byodCta ? { ...pck.byodCta } : pck.cta;
          }
          return pck;
        });
      }
      // streamtv overrides the headline with the stream-specific variant. Build a
      // local merged copy — mutating `content.commonData` would corrupt the shared
      // cached SB story envelope across requests.
      const resolvedCommonData =
        domName === "streamtv" ?
          { ...content?.commonData, headline: content?.commonData?.streamHeadline } :
          content?.commonData;

      if (queryParams?.channels === "satellite") {
        packages?.forEach(pkg => {
          pkg.packageId = pkg?.packageId?.replace('ATT_TV_', 'DTV_');
          const normalizeDtvId = id => id.replace(/^DTV_TV_/, "DTV_");
          const isDtvTvEquivalent = (pkgId, configId) => normalizeDtvId(pkgId) === normalizeDtvId(configId);

          const match = packagesConfig?.find?.(item => isDtvTvEquivalent(pkg.packageId, item.id));
          if (match) {
            pkg.packageConfig = match;
          }
        });
      }
      const updatedEmbeddedSvods = (embeddedSvods || [])?.map(svod => ({
        ...svod,
        displayText: svod?.name
      }));
      const filteredEmbeddedSvods = (updatedEmbeddedSvods || []).filter(svod => !svod?.disableForMdu);
      const myFreeDtvPkg = packagesConfig?.find(
        pkg => pkg.id === "MYFREE_DTV"
      );
      const myFreeDtvChannelCnt = myFreeDtvPkg?.channel?.count ? `${myFreeDtvPkg?.channel?.count}+ Channels` : content.myFreeDTVChnls?.chnlCount;
      const contentText: any = {};
      contentText.localAndRegionalPackageGroupLabel =
        content.localAndRegionalPackageGroupLabel;
      contentText.nationalAndRegionalChannelLabelDesc =
        content.nationalAndRegionalChannelLabelDesc;
      contentText.nationalPackageGroupLabel = content.nationalPackageGroupLabel;
      contentText.searchLabel = content.searchLabel;
      contentText.searchPlaceholderText = content.searchPlaceholderText;
      // Multi-membership map for the CATEGORY view (mirrors /channel-lineup): reuse the
      // lineup's bucketChannelsByCategory so a channel lands under EVERY matching content
      // accordion (e.g. a premium movie channel under BOTH "Movies & events" AND
      // "Premium"). The modal's DDS provider rebuilds each Channel from a fixed whitelist
      // that drops categories[]/engCategories, so this can't ride on the channel — carry
      // it as a side map { bucketKey: channelId[] } on channelData (a plain prop the
      // provider passes through). bucketChannelsByCategory already excludes structural
      // Local/RSN, so those stay on the scalar-category path in the grid.
      const channelCategoryMembership = categoryConfig
        ? buildChannelCategoryMembership(filteredChannels, orderedCategoryConfig)
        : undefined;
      const channelData: any = {
        channelCategoryMembership,
        // Flag ON: "label,label" CSV strings — the getKVsFromStrings contract the CCT
        // provider parses with String.split (emitting {key,value} objects here threw
        // `o.split is not a function` and blanked the modal). Flag OFF: AEM options array.
        channelCategories: categoryConfig
          ? orderedCategoryConfig.map((c: any) => `${c.label},${c.label}`)
          : (aemChannelCategories?.options ?? content?.channelCategories?.options ?? []),
        channels: parseChannelsData(
          filteredChannels,
          packageIds,
          packageCategories,
          packages,
          queryParams,
          myFreeDTVConfig,
          domName,
          langQP,
          orderedCategoryConfig,
          entertainmentCategoryKey
        ),
        commonData: resolvedCommonData,
        contentText,
        localAndRegionalChannels: content.localAndRegionalChannels,
        legalInNationalTab: content.legalInNationalTab,
        legalInRSNTab: content.legalInRSNTab,
        // legalBelowRSNTab: content.legalBelowRSNTab,
        // legalBelowRSNNew: content.legalBelowRSNNew,
        packageCategories: resolvedPackageCategories?.options ?? [],
        packages,
        service: {
          id: content.serviceId,
          logo: content.serviceLogoPath,
          name: content.serviceNameLabel
        },
        sortOptions: resolvedSortOptions?.options ?? [],
        defaultSortOptions: resolvedDefaultSortOptions,
        showCtaCI: content.showCtaCI,
        internetChnlsPDFLink: content.internetChnlsPDFLink,
        satelliteChnlsPDFLink: content.satelliteChnlsPDFLink,
        internetSpanishChnlsPDFLink: content.internetSpanishChnlsPDFLink,
        satelliteSpanishChnlsPDFLink: content.satelliteSpanishChnlsPDFLink,
        myFreeDTVChnls: {
          ...content.myFreeDTVChnls,
          chnlCount: myFreeDtvChannelCnt
        },
        localMySportsGenre: content?.localMySportsGenre,
        streamChnlsPDFLink: content?.commonData?.streamChnlsPDFLink,
        streamSpanishChnlsPDFLink:
          content?.commonData?.streamSpanishChnlsPDFLink,
        mySportsBetaPDFLink: content?.commonData?.mySportsBetaPDFLink,
        domName,
        mySportsEligibleCookie,
        embeddedSvods: (queryParams?.channels?.toLowerCase() === "internet" && queryParams?.referrer?.toLowerCase() === "mdu") ? filteredEmbeddedSvods : updatedEmbeddedSvods,
        getOffersDTViEmbeddedIncludedSvods: dtviEmbeddedSvodOffers,
        getOffersDTVsEmbeddedIncludedSvods: dtvsEmbeddedSvodOffers,
        isEmbeddedSvodsEnabled,
        // Generated by Copilot- Start - 2026-03-18 21:24:19
        genreNationalMapping: genreNationals?.genreNationalMapping || []
        // Generated by Copilot- End - 2026-03-18 21:24:19
      };

      const analyticsData = new ReportingService().fixAnalyticsValues(
        {
          ...content,
          ...{ analytics: { Infinity: content.analytics } }
        },
        null,
        ComponentNames.CHANNEL_COMPARISON_TOOL,
        index
      );

      let edspFlow = true;
      const { defaultFlow } = content?.commonData ?? {};

      if (defaultFlow && defaultFlow.toLowerCase() === "edsp") {
        edspFlow = true;
        if (
          (queryParams && queryParams.edsp === "true") ||
          queryParams.edsp === undefined
        ) {
          edspFlow = true;
        } else {
          edspFlow = false;
        }
      } else if (!defaultFlow || defaultFlow.toLowerCase() === "contract") {
        edspFlow = false;
        if (
          queryParams.edsp === undefined ||
          (queryParams && queryParams.edsp === "false")
        ) {
          edspFlow = false;
        } else if (queryParams && queryParams.edsp === "true") {
          edspFlow = true;
        }
      }
      if (content.serviceId === "dtvs") {
        edspFlow = false;
      }
      // Spanish UX rule: Spanish users always see the contract flow, regardless
      // of the default flow setting.
      if (queryParams?.lang?.toLowerCase?.() === "spanish") {
        edspFlow = false;
      }
      // SETUP PROPS: DATA + OPTIONS TO RETURN

      const zipcode = typeof queryParams?.zipcode === 'string' && /^\d{5}$/.test(queryParams.zipcode.trim()) ?
        queryParams.zipcode.trim() :
        undefined;
      fixedContent = {
        props: {
          data: {
            analytics: analyticsData.analytics,
            channelData,
            theme: parseChannelCompareTheme(content?.theming),
            searchObj: content?.searchObj || {}
          },
          options: {
            id: componentId,
            index,
            isChannelLineUpPage: content?.isChannelLineUpPage ?? false,
            pageName: cmsPageName,
            edspFlow,
            inPage: queryParams && queryParams.inPage,
            zipcodeModuleConfig: content.zipcodeModuleConfig || {},
            cmprPkg: queryParams && queryParams.cmprPkg,
            modalSrc: queryParams && queryParams.modalSrc,
            showCTA: queryParams && queryParams.showCTA,
            // channelsQP: on the streamtv domain, fall back to "INTERNET" only
            // when no `?channels=` param is present (the param wins when set).
            channelsQP: content?.isChannelLineUpPage ?
              (content?.channelsQP && content?.channelsQP) || "INTERNET" :
              domName === "streamtv" ?
                (queryParams?.channels?.toUpperCase?.() || "INTERNET") :
                (queryParams && queryParams.channels) || "",
            isLocal: (queryParams && queryParams.isLocal) || "true",
            tab: queryParams && queryParams.tab,
            lang: queryParams && queryParams.lang,
            category: queryParams && queryParams.category,
            pkg:
              queryParams &&
                queryParams?.lang === "spanish" &&
                domName === "streamtv" ?
                "optimomas" :
                queryParams.pkg,
            pageView: queryParams && queryParams.pageView,
            showPkg: queryParams && queryParams.showPkg,
            mySportsFlag,
            qp: queryParams && queryParams.channels,
            rsnTierLegalTextObj,
            useFFPMode: queryParams && queryParams.myFreeDtv === "true",
            ...(zipcode ? { zipcode } : {})
          }
        },
        type: ComponentNames.CHANNEL_COMPARISON_TOOL_V2
      }
    }
    return fixedContent;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      cmsPageName,
      componentId,
      index
    });
    return {
      props: {
        data: {},
        options: {
          id: componentId,
          index,
          pageName: cmsPageName
        }
      },
      type: ComponentNames.CHANNEL_COMPARISON_TOOL
    };
  }
};

/**
 * Unions channel envelopes into one list, keeping the first occurrence of each
 * `channelNameLabel`. Channels without a label are skipped (nothing to dedupe on).
 */
export const filterChannels = (content: any) => {
  const packageIdsArr = content.packages.map(pack => pack.packageId);
  const packageIds = [];
  for (const pkgIdStr of packageIdsArr) {
    const pkgArr = pkgIdStr.split("TV_");
    const pkgId = pkgArr[pkgArr.length - 1];
    packageIds.push(`ATT_TV_${pkgId}`);
    packageIds.push(`DTV_${pkgId}`);
  }
  const channelss = content.channels;
  const filteredChannels =
    channelss &&
    channelss.filter(e => {
      // Satellite-only view: exclude FAST channels (app/online-only, not on
      // satellite) from the display + its counts (WAT-10783). Display-layer
      // only; no-op in the mixed stream+satellite modal.
      if (isExcludedFastChannel(e, content?.isSatelliteContext)) {
        return false;
      }
      if (e.IsActive && packageIds && packageIds.length) {
        let chnlPackages = [""];
        if (e.packageListSystem && Array.isArray(e.packageListSystem)) {
          chnlPackages = chnlPackages.concat(e.packageListSystem);
        }
        if (e.addonPackageList && Array.isArray(e.addonPackageList)) {
          chnlPackages = chnlPackages.concat(e.addonPackageList);
        }
        if (e.promoPackageList && Array.isArray(e.promoPackageList)) {
          chnlPackages = chnlPackages.concat(e.promoPackageList);
        }
        if (e.excludedPackageList && Array.isArray(e.excludedPackageList)) {
          for (const i of e.excludedPackageList) {
            if (chnlPackages.includes(i)) {
              const id = chnlPackages.indexOf(i);
              chnlPackages.splice(id, 1);
            }
          }
        }
        let check = false;
        let idx = 0;
        while (idx < packageIds.length && !check) {
          const id: string = packageIds[idx];
          check = chnlPackages.includes(id);
          idx += 1;
        }
        return check;
      }
      return false;
    });

  return filteredChannels;
};
export const parseChannelsData = (
  channels,
  _packagesIds: string[],
  packageCategories: KeyValuePair[],
  packages: any[],
  queryParams,
  myFreeDTVConfig: any,
  domName?: string,
  langQP?: string,
  // Ordered category config + Entertainment bucket KEY for resolving a flag-ON
  // service channel's scalar categoryId to a CONTENT accordion label (the modal
  // buckets by label equality). Both default empty so the flag-OFF / AEM call —
  // where channels already carry a categoryId slug — keeps its prior behavior.
  categoryConfig: any[] = [],
  entertainmentCategoryKey?: string
): ChannelFeed[] => {
  try {
    logAdapterEvent(ADAPTER_NAME, "Parsing channel data", {
      channelCount: channels?.length || 0,
      packageCount: packages?.length || 0,
      domName,
      langQP
    });

    const isMyFreeDTVEnabled: boolean =
      myFreeDTVConfig && myFreeDTVConfig?.enableForChannelCompareTool;
    if (channels && Array.isArray(channels)) {
      const parseData = [] as ChannelFeed[];
      for (const c of channels) {
        let packageList = c.packageListSystem;
        if (
          packageList &&
          packageList.length > 0 &&
          c.excludedPackageList &&
          c.excludedPackageList.length > 0
        ) {
          packageList = packageList.filter(
            (packge: string) => !c.excludedPackageList.includes(packge)
          );
        }
        // INCLUDED wins over ADD-ON per package (mirror the lineup's single-list
        // precedence). withTierAliases appends signature-tier aliases (ATT_TV_/DTV_
        // CHOICE/ULTIMATE/PREMIER) to addonPackageList for any channel carrying a
        // Sports-Pack-style addon; for a channel already IN those tiers (e.g. ACC
        // Network HD) that wrongly drove the Add-on section's $ indicator to ADDON.
        // Strip already-included packages so addOnPkgIndicators resolves IN, not $.
        const includedPkgSet = new Set<string>(c.packageListSystem ?? []);
        const addonPkgList = (c["addonPackageList"] ?? []).filter((p: string) => !includedPkgSet.has(p));
        const promoPkgList = (c["promoPackageList"] ?? []).filter((p: string) => !includedPkgSet.has(p));
        const addOnPkgIndicators = {
          "2yr": {},
          edsp: {}
        };
        const packageIndicators = {
          "2yr": {},
          edsp: {}
        } as any;
        packageCategories.forEach(packagCat => {
          const categoryPackages = packages.filter(
            pack => packagCat.key === pack.categoryId
          );
          packageIndicators["2yr"][`${packagCat.key}`] = categoryPackages.map(
            packag => {
              const pkgArr = packag.packageId.split("TV_");
              const pkgId = pkgArr[pkgArr.length - 1];
              if (
                // NOTE: in Included section - priority order is Included - Addon - Promo
                c.packageListSystem &&
                (c.packageListSystem.includes(`ATT_TV_${pkgId}`) ||
                  c.packageListSystem.includes(`DTV_${pkgId}`))
              ) {
                // NOTE: To check addon, promo and included for Addon section (start)
                if (
                  // NOTE: in addon section - priority order is Addon - promo - included
                  addonPkgList &&
                  (addonPkgList.includes(`ATT_TV_${pkgId}`) ||
                    addonPkgList.includes(`DTV_${pkgId}`))
                ) {
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                    addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators["2yr"][`${packagCat.key}`],
                        PACKAGE_INDICATORS.ADDON
                      ] :
                      [PACKAGE_INDICATORS.ADDON];
                } else if (
                  promoPkgList &&
                  (promoPkgList.includes(`ATT_TV_${pkgId}`) ||
                    promoPkgList.includes(`DTV_${pkgId}`))
                ) {
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                    addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators["2yr"][`${packagCat.key}`],
                        PACKAGE_INDICATORS.PROMO
                      ] :
                      [PACKAGE_INDICATORS.PROMO];
                } else {
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                    addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators["2yr"][`${packagCat.key}`],
                        PACKAGE_INDICATORS.IN
                      ] :
                      [PACKAGE_INDICATORS.IN];
                } // NOTE: TO check addon, promo and included for Addon section (end)
                return PACKAGE_INDICATORS.IN;
              } else if (
                // Mirror /channel-lineup precedence IN -> PROMO -> ADDON
                // (channel-lineup-parser.ts:54-65). withTierAliases appends the signature
                // tiers to BOTH addon and promo lists for promo families (HBO/Cinemax), so
                // promo MUST be checked before addon or the star renders as a $ add-on.
                promoPkgList &&
                (promoPkgList.includes(`ATT_TV_${pkgId}`) ||
                  promoPkgList.includes(`DTV_${pkgId}`))
              ) {
                addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                    [
                      ...addOnPkgIndicators["2yr"][`${packagCat.key}`],
                      PACKAGE_INDICATORS.PROMO
                    ] :
                    [PACKAGE_INDICATORS.PROMO];
                return PACKAGE_INDICATORS.PROMO;
              } else if (
                addonPkgList &&
                (addonPkgList.includes(`ATT_TV_${pkgId}`) ||
                  addonPkgList.includes(`DTV_${pkgId}`))
              ) {
                addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                    [
                      ...addOnPkgIndicators["2yr"][`${packagCat.key}`],
                      PACKAGE_INDICATORS.ADDON
                    ] :
                    [PACKAGE_INDICATORS.ADDON];
                return PACKAGE_INDICATORS.ADDON;
              }
              addOnPkgIndicators["2yr"][`${packagCat.key}`] =
                  addOnPkgIndicators["2yr"][`${packagCat.key}`] ?
                    [...addOnPkgIndicators["2yr"][`${packagCat.key}`], null] :
                    [null];
              return null;
            }
          );
          let localPackages = packages;
          const edspCategoryPackages = localPackages.filter(
            _pack => (localPackages = localPackages.map((pkg, index) => ({
              ...pkg,
              packageIndicatorIndex: index
            })))
          );
          packageIndicators.edsp[`${packagCat.key}`] =
            edspCategoryPackages.map(packag => {
              const pkgArr = packag.packageId.split("TV_");
              const pkgId = pkgArr[pkgArr.length - 1];
              if (
                // NOTE: in Included section - priority order is Included - Addon - Promo
                c.packageListSystem &&
                (c.packageListSystem.includes(`ATT_TV_${pkgId}`) ||
                  c.packageListSystem.includes(`DTV_${pkgId}`))
              ) {
                // NOTE: To check addon, promo and included for Addon section (start)
                if (
                  // NOTE: in addon section - priority order is Addon - promo - included
                  addonPkgList &&
                  (addonPkgList.includes(`ATT_TV_${pkgId}`) ||
                    addonPkgList.includes(`DTV_${pkgId}`))
                ) {
                  addOnPkgIndicators.edsp[`${packagCat.key}`] =
                    addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators.edsp[`${packagCat.key}`],
                        PACKAGE_INDICATORS.ADDON
                      ] :
                      [PACKAGE_INDICATORS.ADDON];
                } else if (
                  promoPkgList &&
                  (promoPkgList.includes(`ATT_TV_${pkgId}`) ||
                    promoPkgList.includes(`DTV_${pkgId}`))
                ) {
                  addOnPkgIndicators.edsp[`${packagCat.key}`] =
                    addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators.edsp[`${packagCat.key}`],
                        PACKAGE_INDICATORS.PROMO
                      ] :
                      [PACKAGE_INDICATORS.PROMO];
                } else {
                  addOnPkgIndicators.edsp[`${packagCat.key}`] =
                    addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                      [
                        ...addOnPkgIndicators.edsp[`${packagCat.key}`],
                        PACKAGE_INDICATORS.IN
                      ] :
                      [PACKAGE_INDICATORS.IN];
                } // NOTE: TO check addon, promo and included for Addon section (end)
                return PACKAGE_INDICATORS.IN;
              } else if (
                // Mirror /channel-lineup precedence IN -> PROMO -> ADDON (see 2yr block).
                promoPkgList &&
                (promoPkgList.includes(`ATT_TV_${pkgId}`) ||
                  promoPkgList.includes(`DTV_${pkgId}`))
              ) {
                addOnPkgIndicators.edsp[`${packagCat.key}`] =
                  addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                    [
                      ...addOnPkgIndicators.edsp[`${packagCat.key}`],
                      PACKAGE_INDICATORS.PROMO
                    ] :
                    [PACKAGE_INDICATORS.PROMO];
                return PACKAGE_INDICATORS.PROMO;
              } else if (
                addonPkgList &&
                (addonPkgList.includes(`ATT_TV_${pkgId}`) ||
                  addonPkgList.includes(`DTV_${pkgId}`))
              ) {
                addOnPkgIndicators.edsp[`${packagCat.key}`] =
                  addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                    [
                      ...addOnPkgIndicators.edsp[`${packagCat.key}`],
                      PACKAGE_INDICATORS.ADDON
                    ] :
                    [PACKAGE_INDICATORS.ADDON];
                return PACKAGE_INDICATORS.ADDON;
              }
              addOnPkgIndicators.edsp[`${packagCat.key}`] =
                  addOnPkgIndicators.edsp[`${packagCat.key}`] ?
                    [...addOnPkgIndicators.edsp[`${packagCat.key}`], null] :
                    [null];
              return null;
            });
        });
        // let chnlPackages = [];
        let includedChnlPackages = [];
        let addonPromoChnlPackages = [];
        if (c.packageListSystem && Array.isArray(c.packageListSystem)) {
          includedChnlPackages = includedChnlPackages.concat(
            c.packageListSystem
          );
          // chnlPackages = chnlPackages.concat(c.packageListSystem);
        }
        if (addonPkgList && Array.isArray(addonPkgList)) {
          addonPromoChnlPackages = addonPromoChnlPackages.concat(
            addonPkgList
          );
          // chnlPackages = chnlPackages.concat(addonPkgList);
        }
        if (promoPkgList && Array.isArray(promoPkgList)) {
          addonPromoChnlPackages = addonPromoChnlPackages.concat(
            promoPkgList
          );
          // chnlPackages = chnlPackages.concat(promoPkgList);
        }
        const { lang } = queryParams;
        const pkgCategory = lang ?
          lang.toUpperCase() :
          packageCategories[0].key;
        const includedChnlCategoryPackages = includedChnlPackages.filter(
          pack => {
            const pkgRef = chnlPkgReference().find(pkg => pkg.name === pack);
            return pkgRef && pkgRef.categoryId === pkgCategory;
          }
        );
        let isStreamInc = false;
        let isSatelliteInc = false;
        let isMyFreeDTV = false;
        for (const pkg of includedChnlCategoryPackages) {
          if (!isStreamInc) {
            isStreamInc = pkg.includes("ATT_TV_");
          }
          if (!isSatelliteInc) {
            isSatelliteInc = pkg.includes("DTV_");
          }
          if (isMyFreeDTVEnabled && !isMyFreeDTV) {
            isMyFreeDTV = pkg.includes("MYFREE_DTV");
          }
        }

        const addonPromoChnlCategoryPackages = addonPromoChnlPackages.filter(
          pack => {
            const pkgRef = chnlPkgReference().find(pkg => pkg.name === pack);
            return pkgRef && pkgRef.categoryId === pkgCategory;
          }
        );
        let isStreamAddonPromo = false;
        let isSatelliteAddonPromo = false;
        for (const pkg of addonPromoChnlCategoryPackages) {
          if (!isStreamAddonPromo) {
            isStreamAddonPromo = pkg.includes("ATT_TV_");
          }
          if (!isSatelliteAddonPromo) {
            isSatelliteAddonPromo = pkg.includes("DTV_");
          }
        }
        const channelFeedData: ChannelFeed = {
          IsActive: c.IsActive,
          callLetter: c.channelCallLetterLabel,
          // flag-ON the channels-service entry has no scalar categoryId; resolve it
          // to a CONTENT accordion's bucket KEY (= label) from categories[] — the
          // value the modal's DDS provider buckets on (channel.category === key).
          // resolveChannelCategoryKey returns the first matching accordion label,
          // else the Entertainment label (entertainmentCategoryKey) for format-only
          // channels (e.g. categories ["4K","Mixed Authorization"]) — consistent
          // with the lineup/interactive Entertainment fallback. flag-OFF/AEM keeps
          // its own categoryId slug (the leading `c.categoryId ??`); with the
          // default empty categoryConfig, resolveChannelCategoryKey returns the
          // passed fallback (undefined) so a channel with neither is undefined as
          // before.
          categoryId: c.categoryId ?? resolveChannelCategoryKey(c.categories, categoryConfig, entertainmentCategoryKey ?? c.categories?.[0]),
          // categoryIdSystem: c.categoryIdSystem,
          id: c.channelId,
          label: c.channelNameLabel,
          // labelSystem: c.channelNameLabelSystem,
          logo: c.channelLogoPath,
          resolution: c.displayResolutionLabel,
          packageIndicators,
          isStreamInc,
          isSatelliteInc,
          isMyFreeDTV,
          isStreamAddonPromo,
          isSatelliteAddonPromo,
          chnlNum: c.tunerPosition,
          // chnlPackages, //TODO remove after testing in env
          addOnPkgIndicators,
          includedChnlPackages,
          addonPromoChnlPackages
        };
        parseData.push(channelFeedData);
      }

      return parseData;
    }
    return [];
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Parsing channel data",
      channelCount: channels?.length || 0,
      packageCount: packages?.length || 0
    });
    return [];
  }
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface IDynamicPackageCompareToolAdapeterArgs
  extends IChannelCompareToolPageAdapterArgs { }

export function dynamicChannelCompareToolPageAdapter(
  args: IDynamicPackageCompareToolAdapeterArgs
) {
  try {
    logAdapterEvent(
      ADAPTER_NAME,
      "Dynamic import of Channel Compare Tool Page Adapter",
      {
        cmsPageName: args?.cmsPageName,
        componentId: args?.componentId,
        index: args?.index,
        rsnTierLegalTextObj: args?.rsnTierLegalTextObj
      }
    );
    return ChannelCompareToolPageAdapter(args);
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "Dynamic import of Channel Compare Tool Page Adapter",
      cmsPageName: args?.cmsPageName,
      componentId: args?.componentId,
      index: args?.index
    });
    return args?.content;
  }
}
